import StationGisPicker from "#/commons/map/StationGisPicker";
import GISTools from '#/lib/GISTools';
import StationClient from "#/lib/StationClient";
import Tooltip from "@material-ui/core/Tooltip";
import chroma from 'chroma-js';
import L from 'leaflet';
import moment from 'moment';
import React from 'react';
import { FaAngleLeft, FaCrosshairs, FaFileCsv, FaMapMarkerAlt } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";

import WidthUtils from "#/lib/WidthUtils";
import DateRangePicker from '@wojtekmaj/react-datetimerange-picker';

import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

import AuthenticationService, { ROLE_ADMIN, ROLE_OFFICER } from "#/lib/AuthenticationService";
import { MEASUREMENT_CATEGORY_ICONS, MEASUREMENT_LABELS } from "#/lib/MeasurementCategory";
import SwitchComponent from "./SwitchComponent";
import PlaceholderComponent from "./PlaceholderComponent";
import StubComponent from "./StubComponent";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

export const defaultZone = {
	value: null,
	label: "Tutte le zone"
}

export const defaultStation = {
	value: null,
	label: <>&nbsp;</>
}

export const defaultYear = moment().year();
export const defaultSelectedRage = [moment().startOf("day"), moment().endOf("day")];

const ReactSwal = withReactContent(Swal);
const loadingSwal = Swal.mixin({
	allowOutsideClick: false,
	allowEscapeKey: false,
	didOpen: () => {
		Swal.showLoading();
	},
});

/**
 * Base class for DailyStatsPage and MonthlyStatsPage.
 * Contains all shared GIS/station selection logic, sidebar, and render structure.
 *
 * Subclasses must implement:
 *  - fetchStationsForCategory(selectedCategory, onSuccess, onError)
 *  - renderCalendarPicker()   → JSX for the date/period picker in CALENDAR view
 *  - buildTable()             → JSX for the data table in DATA view
 *  - createCSVFileName()      → returns a string for the CSV filename
 */
export default class BaseStatsPage extends React.Component {

	constructor(props) {
		super(props);
		this.state = {
			allStations: false,
			selectedCategory: '',
			stationsGeoJSON: {},
			year: defaultYear,
			selectedRange: defaultSelectedRage,

			stationMapByCode: null,
			zoneOptions: [],
			stationOptions: [],

			selectedZone: defaultZone,
			selectedStation: defaultStation,

			interval: null,
			mapKey: 0,
			tableKey: 0,

			showGisPicker: true,

			intervals: [],
		}
	}

	componentDidMount() { }

	// ─── Abstract methods ────────────────────────────────────────────────────────

	/**
	 * Must be implemented by subclasses.
	 * Should call onSuccess(stationsGeoJSON) or onError(err).
	 */
	fetchStationsForCategory(selectedCategory, onSuccess, onError) {
		loadingSwal.fire('Recupero Stazioni in corso...');
		StationClient.getAllStationsGeojsonByCategory(
			selectedCategory,
			(stationsGeoJSON) => {
				loadingSwal.close();
				onSuccess(stationsGeoJSON);
			},
			(err) => {
				loadingSwal.close();
				ReactSwal.fire('Si è verificato un errore durante il caricamento delle stazioni', '', 'error');
				onError(err);
			}
		);
	}

	/** Must be implemented by subclasses. Returns JSX for the date/period picker. */
	renderCalendarPicker() {
		throw new Error("BaseStatsPage: renderCalendarPicker() must be implemented by subclass");
	}

	/** Must be implemented by subclasses. Returns JSX for the data table. */
	buildTable() {
		return (
			<>
				<Skeleton height={"10vh"} duration={8} />
				<Skeleton height={"70vh"} duration={8} />
			</>
		);
	}

	/** Must be implemented by subclasses. Returns a string for the CSV filename. */
	createCSVFileName() {
		throw new Error("BaseStatsPage: createCSVFileName() must be implemented by subclass");
	}

	// ─── Common methods ──────────────────────────────────────────────────────────

	onCategoryChange = (selectedCategory) => {
		if (!!selectedCategory) {
			this.fetchStationsForCategory(
				selectedCategory,
				(stationsGeoJSON) => {
					if (stationsGeoJSON.features) {
						let selectedZone = defaultZone;
						let selectedStation = defaultStation;

						let stationMapByCode = stationsGeoJSON.features.reduce((map, f) => {
							map[f.properties.code] = f.properties.name;
							return map;
						}, {});

						let zoneOptions = this.buildZoneOptions();
						let stationOptions = this.buildStationOptions(stationsGeoJSON, selectedZone.value);
						let stationsOutsideRegion = GISTools.getStationsOutsideCalabria(stationsGeoJSON);



						this.setState({
							stationMapByCode,
							selectedCategory,
							zoneOptions,
							stationOptions,
							stationsOutsideRegion,
							stationsGeoJSON,
							selectedZone,
							selectedStation,
							mapKey: (this.state.mapKey + 1) % 1000
						});
					} else {
						ReactSwal.fire('Non sono presenti stazioni con i parametri inseriti', '', 'error');
						this.setState({
							stationOptions: [],
							allStations: false,
							selectedCategory,
							stationsGeoJSON: {},
							selectedStation: defaultStation,
							mapKey: (this.state.mapKey + 1) % 1000
						});
					}
				},
				(err) => {
					console.error(err);
				}
			);
		} else {
			this.setState({
				stationOptions: [],
				allStations: false,
				selectedCategory,
				stationsGeoJSON: {},
				selectedStation: defaultStation,
				mapKey: (this.state.mapKey + 1) % 1000
			});
		}
	}

	buildZoneOptions() {
		let zoneOptions = [];
		let zones = GISTools.getAllZonePolygon();

		zoneOptions.push(defaultZone);
		zones.forEach(zone => {
			let id = zone.properties.ZONE;
			zoneOptions.push({
				value: parseInt(id),
				label: "ZONA " + id,
				icon: <FaCrosshairs className="mr-2"></FaCrosshairs>
			});
		});
		zoneOptions.push({
			value: 9999,
			label: "Territorio extraregionale",
			icon: <FaCrosshairs className="mr-2"></FaCrosshairs>
		});

		return zoneOptions;
	}

	buildStationOptions(stationsGeoJSON, zoneId = null) {
		let stationOptions = [];
		let stations = stationsGeoJSON;

		if (!!zoneId) {
			if (zoneId === 9999) {
				stations = this.state.stationsOutsideRegion;
			} else {
				let zonePolygon = GISTools.getZonePolygonByField("ZONE", zoneId);
				let stationWithinZone = GISTools.intersect(stations, zonePolygon);
				stations = stationWithinZone;
			}
		}

		let stationsProperties = GISTools.getPropertiesFromFeature(stations);
		stationOptions.push(defaultStation);
		stationsProperties.forEach(properties => {
			stationOptions.push({
				label: properties.name,
				value: properties.code,
				icon: <FaMapMarkerAlt className="mr-2"></FaMapMarkerAlt>
			});
		});

		return stationOptions;
	}

	zonesStyle(feature) {
		let zone = parseInt(feature.properties.ZONE);
		let style = {};
		const fillActive = { fillColor: "#20468c69", weight: 0.5, opacity: 1, color: "#20468c69", fillOpacity: 0.5 };
		const fillInactive = { fillColor: "#fff0", weight: 0.5, opacity: 1, color: "#20468c", fillOpacity: 0 };

		if (this.state.selectedZone.value === 0) {
			style = this.state.zoneOptions.map(x => x.value).includes(zone) ? fillActive : fillInactive;
		} else {
			style = this.state.selectedZone.value === zone ? fillActive : fillInactive;
		}

		return style;
	}

	onEachStation(feature, layer) {
		layer.on('click', () => this.onSelectStation(feature.properties.code));

		var popup = L.popup({ closeButton: false })
			.setContent('<div><span class="mr-2 fas fa-map-marker-alt"></span>' + feature.properties.name + '</div>');
		layer.bindPopup(popup);
		let timer = null;
		layer.on('mouseover', function () {
			timer = setTimeout(() => this.openPopup(), 1000);
		});
		layer.on('mouseout', function () {
			clearTimeout(timer);
			this.closePopup();
		});
	}

	stationToMarker(feature, latlng) {
		let name = feature.properties.name;
		let isSelected = name === this.state.selectedStation.label;
		return L.marker(latlng, {
			icon: new L.Icon({
				iconSize: [15, 15],
				iconUrl: isSelected ? '/img/marker_point_selected.png' : '/img/marker_point.png',
				popupAnchor: [0, 0]
			})
		});
	}

	onSelectZone(zoneId) {
		let selectedZone = this.state.zoneOptions.find(element => element.value === zoneId);
		let stationOptions = this.buildStationOptions(this.state.stationsGeoJSON, zoneId);
		this.setState({
			stationOptions,
			selectedZone,
			selectedStation: defaultStation,
			mapKey: (this.state.mapKey + 1) % 1000,
			filteredStationsGeoJson: null
		});
	}

	onSelectStation(stationCode) {
		let stationOptions = this.buildStationOptions(this.state.stationsGeoJSON);
		let selectedStation = stationOptions.find(element => element.value === stationCode);
		let stationPoint = GISTools.getStationByField(this.state.stationsGeoJSON, "code", stationCode);
		let zonePolygon = GISTools.getZonePolygonByPoint(stationPoint);
		let zoneId = (!!zonePolygon && !!zonePolygon.properties) ? zonePolygon.properties.ZONE : 9999;
		let selectedZone = this.state.zoneOptions.find(element => element.value === parseInt(zoneId, 10));
		let zoneBbox = null;
		if (zoneId !== 9999) {
			zonePolygon = GISTools.getZonePolygonByField("ZONE", zoneId);
			zoneBbox = GISTools.getBBoxFromPoints(zonePolygon);
		}
		this.setState({
			selectedZone,
			selectedStation,
			mapKey: (this.state.mapKey + 1) % 1000,
			showGisPicker: !!this.state.interval ? false : true,
			zoneBbox
		});
	}

	toggleSidebar(event) {
		if (event === "over") {
			document.getElementById("mySidebar").style.width = "2.5rem";
			document.getElementById("main").style.marginLeft = "1.5rem";
			document.getElementById("sidebar-button").style.color = "#428f81";
		} else {
			document.getElementById("mySidebar").style.width = "1.5rem";
			document.getElementById("main").style.marginLeft = "0";
			document.getElementById("sidebar-button").style.color = chroma('#428f81').alpha(0.3);
		}
	}

	toggleGisPicker = () => {
		if (!this.state.showGisPicker) {
			document.getElementById("main").style.marginLeft = '0px';
		}
		this.setState({ showGisPicker: !this.state.showGisPicker, interval: null });
	}

	isDisabled = (day) => false;

	onToggleAllStations = () => {
		this.setState({ allStations: !this.state.allStations });
	}

	onToggleActiveSensor = () => {
		this.setState({ activeSensor: !this.state.activeSensor }, () => {
			this.onCategoryChange(this.state.selectedCategory);
		});
	}

	isOfficer = () => AuthenticationService.haveRolesPermssions([ROLE_OFFICER, ROLE_ADMIN]);

	downloadDataCsv = () => {
		this.tableRef.table.download("csv", `${this.createCSVFileName()}.csv`);
	};

	getComponentToShow = () => {
		let mobileMode = WidthUtils.isMobile();

		const view = { STUB: "STUB", CALENDAR: "CALENDAR", DATA: "DATA" };
		let currentView = view.STUB;

		if (!this.state.interval && !this.state.selectedStation.value && !this.state.allStations) {
			currentView = view.STUB;
		} else if ((!!this.state.selectedStation.value || !!this.state.allStations) && !this.state.interval) {
			currentView = view.CALENDAR;
		} else if ((!!this.state.selectedStation.value || !!this.state.allStations) && !!this.state.interval) {
			currentView = view.DATA;
		}

		switch (currentView) {
			case view.STUB:
				return !mobileMode ? (
					<PlaceholderComponent
						title="Seleziona una stazione"
						text="Seleziona una categoria e una stazione dal pannello di sinistra per visualizzare i dati."
						icon={<FaMapMarkerAlt size={48} />}
					/>
				) : <></>;

			case view.CALENDAR:
				return !mobileMode ? (
					<div id={"main"}>
						<h3>Seleziona Intervallo di osservazione</h3>
						{this.renderCalendarPicker()}
					</div>
				) : <div id={"main"}></div>;

			case view.DATA:
				return <div id="main">{this.buildTable()}</div>;

			default:
				return !mobileMode ? <StubComponent /> : <></>;
		}
	}

	renderSidePanelContent() {
		let mobileMode = WidthUtils.isMobile();
		return (
			<>
				<SelectCategoryForm
					id="selectCat"
					label="Seleziona una categoria"
					selectedCategory={this.state.selectedCategory}
					handleSelectCategory={this.onCategoryChange}
				/>

				{(!!this.state.selectedCategory && this.state.stationOptions.length > 0) && <>
					<SwitchComponent
						name={"Seleziona Tutte le Stazioni"}
						value={this.state.allStations}
						onChange={() => this.onToggleAllStations()}
					/>

					<div className={mobileMode ? "" : "d-none"}>
						<DateRangePicker
							className={"col-12"}
							locale={"it-IT"}
							onChange={(dates) => this.onMobilePickRange && this.onMobilePickRange(dates)}
							value={[
								this.state.interval && this.state.interval.start && new Date(this.state.interval.start.valueOf()),
								this.state.interval && this.state.interval.end && new Date(this.state.interval.end.valueOf())
							]}
							maxDate={new Date()}
							format={"dd/MM/yyyy"}
							rangeDivider='&nbsp;&nbsp;&nbsp;'
						/>
					</div>

					{!this.state.allStations && (
						<StationGisPicker
							centerMapOnSelect={true}
							geoJSON={!!this.state.filteredStationsGeoJson ? this.state.filteredStationsGeoJson : this.state.stationsGeoJSON}
							zoneOptions={this.state.zoneOptions}
							selectedZone={this.state.selectedZone}
							onSelectZone={(option) => this.onSelectZone(option)}
							stationOptions={this.state.stationOptions}
							selectedStation={this.state.selectedStation}
							onSelectStation={(option) => this.onSelectStation(option)}
							mapHeight={"65vh"}
							zoneBbox={this.state.zoneBbox}
							mapKey={this.state.mapKey}
							onEachStation={(feature, layer) => this.onEachStation(feature, layer)}
							stationToMarker={(feature, latlng) => this.stationToMarker(feature, latlng)}
							zonesStyle={(feature) => this.zonesStyle(feature)}
						/>
					)}
				</>}
			</>
		);
	}

	render() {
		let mobileMode = WidthUtils.isMobile();

		return (
			<div className="generic-page container-fluid data-page">
				<div className="row mt-4">

					{this.state.showGisPicker ? (
						<div className={mobileMode ? "col-12" : "col-4"}>
							{this.renderSidePanelContent()}
						</div>
					) : <></>}

					<div id="mySidebar" className="sidebar" style={{ display: this.state.showGisPicker ? 'none' : 'block' }}
						onMouseOver={() => this.toggleSidebar("over")}
						onMouseOut={() => this.toggleSidebar("out")}
						onClick={() => this.toggleGisPicker()}>
						<Tooltip open={false} title={"Seleziona un altro sensore"}>
							<><FaAngleLeft style={{ height: 'inherit', width: 'inherit' }} id="sidebar-button" color={chroma('#428f81').alpha(0.3)} /></>
						</Tooltip>
					</div>

					<div className={this.state.showGisPicker ? "col-8" : "col-12"} key={this.state.tableKey}>
						{this.getComponentToShow()}
					</div>
				</div>
				<div className="row"></div>
			</div>
		);
	}
}

// ─── Shared UI component ─────────────────────────────────────────────────────

export const SelectCategoryForm = (props) => {
	const options = [
		<MenuItem key={"default"} value={""}></MenuItem>,
		<MenuItem key={"P"} value={"P"}>{MEASUREMENT_CATEGORY_ICONS.P}{MEASUREMENT_LABELS.P}</MenuItem>,
		<MenuItem key={"T"} value={"T"}>{MEASUREMENT_CATEGORY_ICONS.T}{MEASUREMENT_LABELS.T}</MenuItem>,
		<MenuItem key={"I"} value={"I"}>{MEASUREMENT_CATEGORY_ICONS.I}{MEASUREMENT_LABELS.I}</MenuItem>,
	];
	const classname = !!props.className ? props.className : "col-12 row justify-content-center";

	return (
		<FormControl className={classname} key={"select_category_form"}>
			<InputLabel id="h-helper-label">{props.label}</InputLabel>
			<Select
				disabled={false}
				fullWidth
				labelId={props.label}
				id={props.id}
				value={props.selectedCategory}
				onChange={(e) => props.handleSelectCategory(e.target.value)}
			>
				{options}
			</Select>
		</FormControl>
	);
};
